// src/components/news/NewsImageZoomModal.jsx
import React, { useEffect, useRef, useState } from "react";

const NewsImageZoomModal = ({ media, onClose, titleFallback }) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const panStartRef = useRef({ x: 0, y: 0 });
  const didDragRef = useRef(false);

  // Reset zoom state when media changes or modal closes
  useEffect(() => {
    if (!media) {
      setZoomLevel(1);
      setPan({ x: 0, y: 0 });
      setIsDragging(false);
      didDragRef.current = false;
    } else {
      setZoomLevel(1);
      setPan({ x: 0, y: 0 });
      setIsDragging(false);
      didDragRef.current = false;
    }
  }, [media]);

  if (!media) return null;

  const src = media.url;
  if (!src) return null;

  const alt =
    media.alternativeText ||
    media.alt ||
    media.name ||
    titleFallback ||
    "Zoomed image";

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center px-4 sm:px-6"
      onClick={onClose}
    >
      <div
        className="relative max-w-5xl w-full max-h-[90vh] flex flex-col items-center justify-center gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute -top-8 right-0 text-white/80 hover:text-white text-2xl leading-none px-2"
          aria-label="Close zoomed image"
        >
          ×
        </button>

        {/* Zoomable Image */}
        <div className="flex-1 flex items-center justify-center max-h-[80vh] w-full overflow-hidden">
          <img
            src={src}
            alt={alt}
            className="max-w-full max-h-[80vh] w-auto h-auto object-contain rounded-xl shadow-2xl bg-black cursor-grab select-none"
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoomLevel})`,
              transition: isDragging ? "none" : "transform 150ms ease-out",
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              setIsDragging(true);
              didDragRef.current = false;
              dragStartRef.current = { x: e.clientX, y: e.clientY };
              panStartRef.current = { ...pan };
            }}
            onMouseMove={(e) => {
              if (!isDragging) return;
              const deltaX = e.clientX - dragStartRef.current.x;
              const deltaY = e.clientY - dragStartRef.current.y;
              if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) {
                didDragRef.current = true;
              }
              setPan({
                x: panStartRef.current.x + deltaX,
                y: panStartRef.current.y + deltaY,
              });
            }}
            onMouseUp={() => {
              setIsDragging(false);
            }}
            onMouseLeave={() => {
              setIsDragging(false);
            }}
            onTouchStart={(e) => {
              if (e.touches.length !== 1) return;
              const touch = e.touches[0];
              didDragRef.current = false;
              setIsDragging(true);
              dragStartRef.current = { x: touch.clientX, y: touch.clientY };
              panStartRef.current = { ...pan };
            }}
            onTouchMove={(e) => {
              if (!isDragging) return;
              if (e.touches.length !== 1) return;
              const touch = e.touches[0];
              const deltaX = touch.clientX - dragStartRef.current.x;
              const deltaY = touch.clientY - dragStartRef.current.y;
              if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) {
                didDragRef.current = true;
              }
              setPan({
                x: panStartRef.current.x + deltaX,
                y: panStartRef.current.y + deltaY,
              });
            }}
            onTouchEnd={() => {
              setIsDragging(false);
            }}
            onClick={(e) => {
              if (didDragRef.current) {
                didDragRef.current = false;
                return;
              }
              if (zoomLevel > 1) {
                setZoomLevel(1);
                setPan({ x: 0, y: 0 });
              }
            }}
          />
        </div>

        {/* Zoom Controls (100% - 300%) */}
        <div className="w-full max-w-xl bg-black/60 rounded-full px-4 py-3 flex items-center gap-3 text-white text-xs sm:text-sm">
          <button
            type="button"
            className="px-2 py-1 rounded-full bg-white/10 hover:bg-white/20 transition-colors text-sm sm:text-base"
            onClick={() => {
              setZoomLevel((prev) => {
                const next = Math.max(1, prev - 0.25);
                if (next === 1) {
                  setPan({ x: 0, y: 0 });
                }
                return next;
              });
            }}
          >
            -
          </button>
          <input
            type="range"
            min={100}
            max={300}
            step={25}
            value={Math.round(zoomLevel * 100)}
            onChange={(e) => {
              const value = Number(e.target.value);
              const nextZoom = value / 100;
              setZoomLevel(nextZoom);
              if (nextZoom === 1) {
                setPan({ x: 0, y: 0 });
              }
            }}
            className="flex-1 accent-white"
          />
          <span className="w-14 text-center">
            {Math.round(zoomLevel * 100)}%
          </span>
          <button
            type="button"
            className="px-2 py-1 rounded-full bg-white/10 hover:bg白/20 transition-colors text-sm sm:text-base"
            onClick={() => {
              setZoomLevel((prev) => Math.min(3, prev + 0.25));
            }}
          >
            +
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewsImageZoomModal;



